import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { MsalModule, MsalInterceptor, MsalService, MsalGuard, MsalBroadcastService, MsalRedirectComponent } from '@azure/msal-angular';
import { AccountInfo, BrowserCacheLocation, Configuration, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';


const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

const msalConfig: Configuration = {
  /** Auth parameters */
  auth: {
    clientId: 'e0071e90-ff3a-4855-b71a-bcd045347451',
    authority: 'https://login.microsoftonline.com/3bc062e4-ac9d-4c17-b4dd-3aad637ff1ac',
    redirectUri: '/'
  },
  /** Cache config */
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: isIE,
  },
  /** System and logs config */
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        if (logLevel === LogLevel.Error || logLevel === LogLevel.Warning) {
          console.log(message);
        }
      },
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

/** Login available scopes */
const loginRequest = {
  scopes: ['User.Read', 'api://e0071e90-ff3a-4855-b71a-bcd045347451/flexseat'],
};

/** API endpoints to authorize automatically */
const protectedResourceMap = new Map([
  ['https://graph.microsoft.com/beta/', ['User.Read']],
  ['http://localhost:5169/', ['api://e0071e90-ff3a-4855-b71a-bcd045347451/flexseat']],
  [environment.apiUrl, ['api://e0071e90-ff3a-4855-b71a-bcd045347451/flexseat']]
]);

/** Global variable to store the MSAL instance */
let msalInstance: PublicClientApplication;

/** Function to create or get an MSAL Instance */
function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    if ((window as any).msalInstance) {
      console.log('🔄 Reusing existing MSAL instance');
      msalInstance = (window as any).msalInstance;
    } else {
      console.log('🆕 Creating new MSAL instance');
      msalInstance = new PublicClientApplication(msalConfig);
      (window as any).msalInstance = msalInstance;
    }
  }
  return msalInstance;
}

/**
 * Initialize the MSAL instance
 */
export async function initializeMsal() {

  console.log('🚀 Starting MSAL initialization...');

  const instance = getMsalInstance();
    
  /** Inicializa MSAL */
  await instance.initialize();
  console.log('🔧 MSAL instance initialized');

  /** Process pending redirects */
  console.log('🔄 Handling any pending redirects...');
  const redirectResponse = await instance.handleRedirectPromise();
    
  if (redirectResponse) {
    console.log('✅ Redirect response processed:', redirectResponse.account?.username);
    instance.setActiveAccount(redirectResponse.account);
  }

  //! IMPORTANT: Verify if authentication is complete before building the app
  console.log('🔐 Ensuring complete authentication...');
  const authenticatedAccount = await ensureAuthentication(instance);

  /** If it returned null, it means it's redirecting to login */
  if (!authenticatedAccount) {
    console.log('🔄 Redirecting to authentication - stopping bootstrap');
    return; // Stop here, do not initialize Angular yet
  }
    
  console.log('✅ Authentication confirmed for:', authenticatedAccount.username);

  /** Add delay to ensure everything is stabilized */
  await new Promise(resolve => setTimeout(resolve, 200));

}

/**
 *  Ensure that the user is authenticated
 */
async function ensureAuthentication(msalInstance: PublicClientApplication): Promise<AccountInfo | null> {
  try {
    console.log('🔐 Checking authentication status...');

    // 1. Try to find active account
    let activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
        console.log('👤 Set active account:', activeAccount.username);
      }
    }

    /** 2. If there is an active account, try to acquire token silently */
    if (activeAccount) {
      try {
        console.log('🔄 Attempting silent token acquisition...');
        
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes || ['user.read'],
          account: activeAccount
        });
        
        if (tokenResponse && tokenResponse.accessToken) {
          console.log('✅ Silent token acquisition successful');
          return activeAccount;
        }
      } catch (silentError) {
        console.warn('⚠️ Silent token acquisition failed:', silentError);

        /** If the token expired or there are issues, force a new login */
        if (silentError && typeof silentError === 'object' && 'name' in silentError && silentError.name === 'InteractionRequiredAuthError') {
          console.log('🔄 Interaction required - will redirect to login');
          await msalInstance.loginRedirect(loginRequest);
          return null; // Return null because it will redirect
        }
      }
    }

    /** 3. If there is no account or token, initiate login */
    if (!activeAccount) {
      console.log('🔄 No active account - redirecting to login');
      await msalInstance.loginRedirect(loginRequest);
      return null; // Return null because it will redirect
    }
    
    return activeAccount;
    
  } catch (error) {
    console.error('❌ Authentication check failed:', error);
    throw error;
  }
}

export const msalAppConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
        MsalModule.forRoot(
          getMsalInstance(),
          {
            interactionType: InteractionType.Redirect,
            authRequest: loginRequest
          },
          {
            interactionType: InteractionType.Redirect,
            protectedResourceMap: protectedResourceMap
          }
        )
      ),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      MsalRedirectComponent
  ],
};
